import {
  ArrowSwitchIcon,
  BookIcon,
  BrowserIcon,
  ChevronDownIcon,
  CodeReviewIcon,
  CommentIcon,
  DeviceMobileIcon,
  GraphIcon,
  InfinityIcon,
  LocationIcon,
  MentionIcon,
  MortarBoardIcon,
  PackageIcon,
  PlugIcon,
  PlusCircleIcon,
  ProjectIcon,
  PulseIcon,
  RepoPushIcon,
  RocketIcon,
  ShieldCheckIcon,
  ShieldLockIcon,
  SparkleFillIcon,
  TriangleDownIcon,
  TriangleUpIcon,
  VerifiedIcon,
  VersionsIcon,
  XIcon,
} from '@primer/octicons-react'
import {
  ThemeProvider,
  Hero,
  AnchorNav,
  SectionIntro,
  FAQ,
  FAQGroup,
  Bento,
  Box,
  Text,
  River,
  Heading,
  Stack,
  Card,
  OrderedList,
  UnorderedList,
  Button,
  Image,
  LogoSuite,
  Label,
  Link,
  Testimonial,
} from '@primer/react-brand'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

export function EnterpriseAdvancedSecurityIndex() {
  const {requestDemoPath} = useRoutePayload<{requestDemoPath: string}>()
  const {heroContactSalesPath} = useRoutePayload<{heroContactSalesPath: string}>()
  const {navContactSalesPath} = useRoutePayload<{navContactSalesPath: string}>()
  const {pricingContactSalesPath} = useRoutePayload<{pricingContactSalesPath: string}>()

  return (
    <ThemeProvider colorMode="dark" className="lp-Security lp-has-fontSmoothing lp-textWrap--pretty">
      <section id="hero" className="lp-Hero">
        <div className="lp-Hero-visual">
          <canvas className="js-security-hero" />
        </div>
        <div className="lp-Hero-visualFallbackImage">
          <Image src="/images/modules/site/enterprise/advanced-security/hero-visual.jpg" alt="" width={1280} />
        </div>

        <div className="lp-Container">
          <Hero data-hpc align="center" className="lp-Hero-content">
            <Hero.Label color="green-blue" className="lp-Hero-label">
              GitHub Advanced Security
            </Hero.Label>
            <Hero.Heading size="1" weight="bold" className="lp-Hero-heading">
              Application security <br className="lp-breakWhenWide" /> built in, not bolted on.
            </Hero.Heading>
            <Hero.Description className="lp-Hero-description">
              With application security tools at their fingertips, developers fix vulnerabilities up to 7x faster than
              those using third-party tools.<sup>1</sup>
            </Hero.Description>
            <Hero.PrimaryAction href={heroContactSalesPath} className="lp-Hero-ctaButton">
              Contact sales
            </Hero.PrimaryAction>
            <Hero.SecondaryAction href={requestDemoPath} hasArrow={false} className="lp-Hero-ctaButton">
              Request a demo
            </Hero.SecondaryAction>
          </Hero>

          <LogoSuite hasDivider={false} className="lp-Hero-logos">
            <LogoSuite.Heading visuallyHidden>GitHub Security is used by</LogoSuite.Heading>
            <LogoSuite.Logobar marquee marqueeSpeed="slow">
              <Image
                src="/images/modules/site/enterprise/advanced-security/logos/telus.svg"
                alt="Telus's logo"
                height={36}
              />
              <Image
                src="/images/modules/site/enterprise/advanced-security/logos/fidelity.svg"
                alt="Fidelity's logo"
                height={36}
              />
              <Image
                src="/images/modules/site/enterprise/advanced-security/logos/mercado-libre.svg"
                alt="Mercado Libre's logo"
                height={40}
              />
              <Image
                src="/images/modules/site/enterprise/advanced-security/logos/shopify.svg"
                alt="Shopify's logo"
                height={40}
              />
              <Image
                src="/images/modules/site/enterprise/advanced-security/logos/linkedin.svg"
                alt="LinkedIn's logo"
                height={36}
              />
              <Image
                src="/images/modules/site/enterprise/advanced-security/logos/intel.svg"
                alt="Intel's logo"
                height={34}
              />
              <Image src="/images/modules/site/enterprise/advanced-security/logos/3m.svg" alt="3M's logo" height={48} />
              <Image
                src="/images/modules/site/enterprise/advanced-security/logos/kpmg.svg"
                alt="KPMG's logo"
                height={38}
              />
              <Image
                src="/images/modules/site/enterprise/advanced-security/logos/raiffeisen-bank-international.svg"
                alt="Raiffeisen Bank International's logo"
                height={38}
              />
              <Image
                src="/images/modules/site/enterprise/advanced-security/logos/deutsche-borse.svg"
                alt="Deutsche Börse's logo"
                height={48}
              />
            </LogoSuite.Logobar>
          </LogoSuite>

          <Box className="lp-AnchorNav" style={{height: 0}}>
            <AnchorNav hideUntilSticky>
              <AnchorNav.Link href="#enterprise">Enterprise-ready</AnchorNav.Link>
              <AnchorNav.Link href="#features">Features</AnchorNav.Link>
              <AnchorNav.Link href="#pricing">Pricing</AnchorNav.Link>
              <AnchorNav.Link href="#faq">FAQs</AnchorNav.Link>
              <AnchorNav.Action href={navContactSalesPath}>Contact sales</AnchorNav.Action>
              <AnchorNav.SecondaryAction href={requestDemoPath}>Request a demo</AnchorNav.SecondaryAction>
            </AnchorNav>
          </Box>
        </div>
      </section>

      <section id="enterprise">
        <div className="lp-Container">
          <div
            className="lp-Spacer"
            style={{'--lp-space': '64px', '--lp-space-xl': '128px'} as React.CSSProperties}
            aria-hidden
          />

          <SectionIntro align="center" fullWidth>
            <SectionIntro.Label size="large" className="lp-SectionIntroLabel--muted">
              Enterprise-ready
            </SectionIntro.Label>
            <SectionIntro.Heading as="h2" size="2" weight="semibold">
              Fixes in minutes, <br className="lp-break-whenNarrow" /> not months.
            </SectionIntro.Heading>
            <SectionIntro.Description className="lp-Enterprise-sectionIntroDescription">
              With AI-powered static analysis, secret scanning, and software composition analysis, GitHub Advanced
              Security helps developers and security teams work together to accelerate the delivery of secure software.
            </SectionIntro.Description>
          </SectionIntro>

          <div className="lp-Spacer" style={{'--lp-space': '64px'} as React.CSSProperties} aria-hidden />

          <Bento>
            {/* Bento 1 */}
            <Bento.Item columnSpan={{xsmall: 12, medium: 7}} rowSpan={{xsmall: 4, medium: 5}} visualAsBackground>
              <Bento.Content horizontalAlign="center" verticalAlign="center">
                <Bento.Heading
                  as="h3"
                  size="4"
                  weight="semibold"
                  className="lp-letterSpacing700-whenWide lp-fontSize32-whenNarrow"
                >
                  Secure code, accelerated.
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual>
                <Image
                  src="/images/modules/site/enterprise/advanced-security/enterprise-bento-1.jpg"
                  alt=""
                  width="724"
                  height="500"
                  loading="lazy"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 2 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 5}}
              rowSpan={{xsmall: 5, medium: 5}}
              visualAsBackground
              className="lp-Enterprise-bentoItem"
            >
              <Bento.Content
                horizontalAlign="center"
                verticalAlign="center"
                leadingVisual={<PulseIcon />}
                className="lp-Bento--withAccentIcon lp-Bento--iconWithThickerStroke"
              >
                <Bento.Heading
                  as="h3"
                  className="lp-Enterprise-bentoStatsText lp-is-sansSerifAlt lp-letterSpacing700-whenWide"
                  style={{marginBottom: '24px', lineHeight: '100%'}}
                >
                  <span className="lp-Color-accent">7x</span> <br /> faster <br /> fixes
                </Bento.Heading>
                <Text as="div" variant="muted">
                  Detect, prevent, and remediate vulnerabilities more than 7x faster than the industry average.
                  <sup>1</sup>
                </Text>
              </Bento.Content>
            </Bento.Item>

            {/* Bento 3 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 5}}
              rowSpan={{xsmall: 5, medium: 5}}
              visualAsBackground
              className="lp-Enterprise-bentoItem"
            >
              <Bento.Content
                horizontalAlign="center"
                verticalAlign="center"
                leadingVisual={<TriangleDownIcon />}
                className="lp-Bento--withAccentIcon lp-Bento--iconWithThickerStroke"
              >
                <Bento.Heading
                  as="h3"
                  className="lp-Enterprise-bentoStatsText lp-is-sansSerifAlt lp-letterSpacing700-whenWide"
                  style={{marginBottom: '24px', lineHeight: '100%'}}
                >
                  <span className="lp-Color-accent">2.4x</span> <br /> more <br /> precise.
                </Bento.Heading>
                <Text as="div" variant="muted" style={{marginBottom: '0'}}>
                  Finds leaked secrets with <br className="lp-breakWhenWide" /> fewest false positives.<sup>2</sup>
                  <div className="lp-Bento--iconWithThickerStroke" style={{marginTop: '32px'}}>
                    <TriangleUpIcon size={44} className="lp-Color-accent" />
                  </div>
                </Text>
              </Bento.Content>
            </Bento.Item>

            {/* Bento 4 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 7}}
              rowSpan={{xsmall: 4, medium: 5}}
              style={{
                backgroundSize: 'cover',
                backgroundImage: 'url("/images/modules/site/enterprise/advanced-security/enterprise-bento-4.jpg")',
                gridTemplateRows: '1fr 1fr',
                rowGap: '24px',
              }}
            >
              <Bento.Content
                horizontalAlign="center"
                verticalAlign="end"
                leadingVisual={<ShieldLockIcon />}
                className="lp-Bento--iconWithThickerStroke"
              >
                <Bento.Heading
                  as="h3"
                  size="4"
                  weight="semibold"
                  className="lp-letterSpacing700-whenWide lp-fontSize28-whenNarrow"
                >
                  Prevent secret leaks.
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual fillMedia={false} padding="condensed" horizontalAlign="center" verticalAlign="start">
                <Image
                  src="/images/modules/site/enterprise/advanced-security/enterprise-bento-4-visual.webp"
                  alt="Screenshot of a failed git push due to the detection of a secret"
                  width="471"
                  height="128"
                  loading="lazy"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 5 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 7}}
              rowSpan={{xsmall: 4, medium: 5}}
              style={{
                backgroundSize: 'cover',
                backgroundImage: 'url("/images/modules/site/enterprise/advanced-security/enterprise-bento-5.jpg")',
                gridTemplateRows: '1fr 1.5fr',
                rowGap: '24px',
              }}
            >
              <Bento.Content horizontalAlign="center" verticalAlign="end">
                <Bento.Heading
                  as="h3"
                  size="4"
                  weight="semibold"
                  className="lp-letterSpacing700-whenWide lp-fontSize28-whenNarrow"
                >
                  Found means fixed.
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual fillMedia={false} padding="condensed" horizontalAlign="center" verticalAlign="start">
                <Image
                  src="/images/modules/site/enterprise/advanced-security/enterprise-bento-5-visual.webp"
                  alt="Code scanning autofix example that highlights the line of vulnerable code and adds a secure code suggestion"
                  width="560"
                  height="183"
                  loading="lazy"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 6 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 5}}
              rowSpan={{xsmall: 4, medium: 5}}
              visualAsBackground
              className="lp-Enterprise-bentoItem"
            >
              <Bento.Content
                horizontalAlign="center"
                verticalAlign="center"
                leadingVisual={<ShieldCheckIcon />}
                className="lp-Bento--withAccentIcon lp-Bento--iconWithThickerStroke"
              >
                <Bento.Heading
                  as="h3"
                  size="4"
                  weight="semibold"
                  className="lp-letterSpacing700-whenWide lp-fontSize28-whenNarrow"
                >
                  Keep vulnerabilities out of your code.
                </Bento.Heading>
              </Bento.Content>
            </Bento.Item>

            {/* Bento 7 */}
            <Bento.Item
              columnSpan={12}
              rowSpan={{xsmall: 5, medium: 5}}
              visualAsBackground
              className="lp-Enterprise-bentoItem"
            >
              <Bento.Content horizontalAlign="center" verticalAlign="center">
                <Bento.Heading
                  as="h3"
                  size="4"
                  weight="semibold"
                  className="lp-letterSpacing700-whenWide lp-fontSize22-whenNarrow"
                  style={{maxWidth: '840px'}}
                >
                  <Image
                    src="/images/modules/site/enterprise/advanced-security/enterprise-bento-7-icon.svg"
                    alt="Telus's logo"
                    width="30"
                    height="24"
                    loading="lazy"
                  />
                  <div style={{marginBlockStart: '48px', marginBlockEnd: '48px'}}>
                    GitHub Advanced Security helps developers fix potential issues before production.
                  </div>
                  <Image
                    src="/images/modules/site/enterprise/advanced-security/enterprise-bento-7-logo.svg"
                    alt="Mercado libre's logo"
                    width="157"
                    height="40"
                    loading="lazy"
                  />
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual>
                <Image
                  src="/images/modules/site/enterprise/advanced-security/enterprise-bento-7.jpg"
                  alt=""
                  width="1248"
                  height="466"
                  loading="lazy"
                />
              </Bento.Visual>
            </Bento.Item>
          </Bento>

          <div>
            <Image
              src="/images/modules/site/enterprise/advanced-security/enterprise-outro-sm.jpg"
              alt=""
              width={350}
              loading="lazy"
              className="lp-Enterprise-outroImage lp-Enterprise-outroImage--sm"
            />
            <Image
              src="/images/modules/site/enterprise/advanced-security/enterprise-outro.jpg"
              alt=""
              width={1148}
              loading="lazy"
              className="lp-Enterprise-outroImage lp-Enterprise-outroImage--lg"
            />
          </div>
        </div>
      </section>

      <section id="features">
        <div className="lp-Container">
          <SectionIntro align="center" className="lp-Features-sectionIntro">
            <SectionIntro.Label size="large" className="lp-SectionIntroLabel--muted">
              Features
            </SectionIntro.Label>
            <SectionIntro.Heading
              as="h2"
              size="3"
              weight="semibold"
              className="lp-textWrap--balance"
              style={{maxWidth: '32ch'}}
            >
              For developers who love to code.{' '}
              <span className="lp-Color--muted">
                Detect, prevent, and fix vulnerabilities without leaving your flow.
              </span>
            </SectionIntro.Heading>
          </SectionIntro>

          <div
            className="lp-Spacer"
            style={{'--lp-space': '28px', '--lp-space-xl': '40px'} as React.CSSProperties}
            aria-hidden
          />

          {/* Features River */}

          {/* River 1 */}
          <River imageTextRatio="60:40" className="lp-River">
            <River.Content>
              <Heading as="h3" size="5">
                Secure code in your flow.
              </Heading>
              <Text as="p" className="lp-River-text">
                With actionable feedback in the pull request, code scanning helps you find, triage, and prioritize
                security vulnerabilities right in your workflow.
              </Text>
              <Link href="https://docs.github.com/en/enterprise-cloud@latest/code-security/code-scanning/introduction-to-code-scanning/about-code-scanning">
                Explore code scanning
              </Link>
            </River.Content>
            <River.Visual className="lp-River-visual">
              <Image
                src="/images/modules/site/enterprise/advanced-security/features-river-1.webp"
                alt="CodeQL warns of a reflected cross-site scripting vulnerability"
                width="708"
                height="510"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          {/* River 2 */}
          <River imageTextRatio="60:40" className="lp-River">
            <River.Content>
              <Heading as="h3" size="5">
                <Label className="lp-Label--inRiver">Available in public beta – March, 2024</Label>
                AI-powered security fixes.
              </Heading>
              <Text as="p" className="lp-River-text">
                Confidently resolve vulnerabilities before they make it to production with autofix code suggestions in
                the pull request. With GitHub Advanced Security, you don’t need to be a security expert to write secure
                code.
              </Text>
              <Link href="https://docs.github.com/en/code-security/code-scanning/managing-code-scanning-alerts/about-autofix-for-codeql-code-scanning">
                Explore autofix
              </Link>
            </River.Content>
            <River.Visual className="lp-River-visual">
              <Image
                src="/images/modules/site/enterprise/advanced-security/features-river-2.webp"
                alt="Code scanning autofix identifies vulnerable code and provides an explanation, together with a secure code suggestion to remediate the vulnerability"
                width="708"
                height="510"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          {/* River 3 */}
          <River imageTextRatio="60:40" className="lp-River">
            <River.Content>
              <Heading as="h3" size="5">
                Secure your secrets, protect your business.
              </Heading>
              <Text as="p" className="lp-River-text">
                Secret scanning with push protection guards over 200 token types and patterns from more than 180 service
                providers, including secrets unique to your organization.
              </Text>
              <Link href="https://docs.github.com/en/enterprise-cloud@latest/code-security/secret-scanning/about-secret-scanning">
                Explore secret scanning
              </Link>
            </River.Content>
            <River.Visual className="lp-River-visual">
              <Image
                src="/images/modules/site/enterprise/advanced-security/features-river-3.webp"
                alt="GitHub push protection confirms an active secret and blocks the push"
                width="708"
                height="510"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          <div
            className="lp-Spacer"
            style={{'--lp-space': '64px', '--lp-space-xl': '104px'} as React.CSSProperties}
            aria-hidden
          />

          <Testimonial size="large" className="lp-Features-testimonial">
            <Testimonial.Quote className="lp-Features-testimonialQuote">
              Push protection is such a beautiful workflow. We’ve never received a single complaint from developers.
            </Testimonial.Quote>
            <Testimonial.Logo className="lp-Features-testimonialLogo">
              <img
                src="/images/modules/site/enterprise/advanced-security/features-testimonial-logo.svg"
                alt="Telus's logo"
                width="173"
                height="34"
              />
            </Testimonial.Logo>
          </Testimonial>

          {/* Features Bento */}

          {/* Bento 1 */}
          <Bento>
            <Bento.Item columnSpan={12} rowSpan={{xsmall: 5, medium: 6}} className="lp-Features-bentoItem">
              <Bento.Content
                horizontalAlign={{xsmall: 'start', medium: 'center'}}
                verticalAlign="center"
                padding={{xsmall: 'normal', medium: 'spacious'}}
              >
                <Bento.Heading as="h3" size="5" weight="semibold">
                  Dependencies you can depend on.
                </Bento.Heading>
                <Text as="div" variant="muted" style={{maxWidth: '460px'}} className="lp-fontSize--smallWhenNarrow">
                  Secure, manage, and report on software supply chains with automated security and version updates and
                  one-click software bills of material (SBOMs).
                </Text>
                <Link
                  href="https://docs.github.com/en/enterprise-cloud@latest/code-security/supply-chain-security/end-to-end-supply-chain/end-to-end-supply-chain-overview"
                  className="lp-Features-linkInBento"
                >
                  Secure your end-to-end supply chain
                </Link>
              </Bento.Content>
              <Bento.Visual
                fillMedia={false}
                padding="normal"
                horizontalAlign="center"
                verticalAlign="start"
                position="50% 0%"
              >
                <Image
                  src="/images/modules/site/enterprise/advanced-security/features-bento-1.webp"
                  alt="Dependency review identifies high severity vulnerabilities in a .json package"
                  width="915"
                  height="544"
                  loading="lazy"
                  className="lp-borderRadiusNone"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 2 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 6}}
              rowSpan={{xsmall: 5, medium: 6}}
              className="lp-Features-bentoItem lp-Features-bentoItem2"
            >
              <Bento.Content
                horizontalAlign="start"
                verticalAlign="end"
                padding={{xsmall: 'normal', medium: 'spacious'}}
              >
                <Bento.Heading as="h3" size="5" weight="semibold" style={{maxWidth: '20ch'}}>
                  Security status at a glance.
                </Bento.Heading>
                <Text as="div" variant="muted" className="lp-fontSize--smallWhenNarrow">
                  Verify progress and track trends to prioritize remediation tasks across multiple repositories.
                </Text>
                <Link
                  href="https://docs.github.com/en/enterprise-cloud@latest/code-security/security-overview/about-security-overview"
                  className="lp-Features-linkInBento"
                >
                  Discover security overview
                </Link>
              </Bento.Content>
              <Bento.Visual
                fillMedia={false}
                position="0% 0%"
                padding={{xsmall: 'normal', medium: 'spacious'}}
                style={{paddingRight: '0', paddingBottom: '0'}}
              >
                <Image
                  src="/images/modules/site/enterprise/advanced-security/features-bento-2.webp"
                  alt="Trend graph showing a decline in critical vulnerabilities over time"
                  width="550"
                  height="409"
                  loading="lazy"
                  className="lp-borderRadiusNone"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 3 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 6}}
              rowSpan={{xsmall: 6, medium: 6}}
              className="lp-Features-bentoItem"
              style={{gridGap: '0'}}
            >
              <Bento.Content
                horizontalAlign="start"
                verticalAlign="end"
                padding={{xsmall: 'normal', medium: 'spacious'}}
              >
                <Bento.Heading as="h3" size="5" weight="semibold">
                  Your workflows, <br /> your tools.
                </Bento.Heading>
                <Text as="div" variant="muted" className="lp-fontSize--smallWhenNarrow">
                  With support for more than 17,000 app integrations and actions templates, GitHub Advanced Security
                  provides one workflow for your entire toolchain.
                </Text>
                <Link href="https://github.com/marketplace" className="lp-Features-linkInBento">
                  Explore GitHub Marketplace
                </Link>
              </Bento.Content>
              <Bento.Visual>
                <div
                  className="lp-Features-Ticker"
                  role="marquee"
                  aria-label="Scrolling ticker of solution categories found in the GitHub Marketplace"
                >
                  <div className="lp-Features-Ticker-row">
                    <div className="lp-Features-Ticker-scroll">
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <CommentIcon />
                          Chat
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <ProjectIcon />
                          Project management
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <SparkleFillIcon />
                          Code Quality
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <RocketIcon />
                          Deployment
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <InfinityIcon />
                          Continuous Integration
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <MortarBoardIcon />
                          Learning
                        </span>
                      </div>
                    </div>
                    <div className="lp-Features-Ticker-scroll" aria-hidden>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <CommentIcon />
                          Chat
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <ProjectIcon />
                          Project management
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <SparkleFillIcon />
                          Code Quality
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <RocketIcon />
                          Deployment
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <InfinityIcon />
                          Continuous Integration
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <MortarBoardIcon />
                          Learning
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="lp-Features-Ticker-row">
                    <div className="lp-Features-Ticker-scroll">
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <GraphIcon />
                          Monitoring
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <ShieldCheckIcon />
                          Security
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <VersionsIcon />
                          Testing
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <ArrowSwitchIcon />
                          API Management
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <CodeReviewIcon />
                          Code review
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <PackageIcon />
                          Dependency management
                        </span>
                      </div>
                    </div>
                    <div className="lp-Features-Ticker-scroll" aria-hidden>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <GraphIcon />
                          Monitoring
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <ShieldCheckIcon />
                          Security
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <VersionsIcon />
                          Testing
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <ArrowSwitchIcon />
                          API Management
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <CodeReviewIcon />
                          Code review
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <PackageIcon />
                          Dependency management
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="lp-Features-Ticker-row">
                    <div className="lp-Features-Ticker-scroll">
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <BrowserIcon />
                          IDEs
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <LocationIcon />
                          Localization
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <DeviceMobileIcon />
                          Mobile
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <RepoPushIcon />
                          Publishing
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <PlusCircleIcon />
                          Recently added
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <MentionIcon />
                          Support
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <PlugIcon />
                          Utilities
                        </span>
                      </div>
                    </div>
                    <div className="lp-Features-Ticker-scroll" aria-hidden>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <BrowserIcon />
                          IDEs
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <LocationIcon />
                          Localization
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <DeviceMobileIcon />
                          Mobile
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <RepoPushIcon />
                          Publishing
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <PlusCircleIcon />
                          Recently added
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <MentionIcon />
                          Support
                        </span>
                      </div>
                      <div className="lp-Features-Ticker-item">
                        <span className="lp-Features-Ticker-content">
                          <PlugIcon />
                          Utilities
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 4 */}
            <Bento.Item
              columnSpan={12}
              rowSpan={{xsmall: 8, small: 6, medium: 5}}
              flow={{xsmall: 'row', small: 'row', medium: 'row'}}
              className="lp-Features-bentoItem lp-Features-bentoItem4"
            >
              <Bento.Content
                horizontalAlign="start"
                verticalAlign="center"
                padding={{xsmall: 'normal', medium: 'spacious'}}
                leadingVisual={<VerifiedIcon />}
                className="lp-Features-bentoItem4-content lp-Bento--withAccentIcon lp-Bento--iconWithThickerStroke"
              >
                <Bento.Heading as="h3" size="5" weight="semibold" style={{marginTop: 'auto'}}>
                  Security expertise at your command.
                </Bento.Heading>
                <Text as="p" variant="muted" className="lp-fontSize--smallWhenNarrow">
                  Powered by security experts and a global community of more than 100m developers, GitHub Advanced
                  Security provides the insights and automation you need to ship secure software on schedule.
                </Text>
                <Link href="https://securitylab.github.com/" className="lp-Features-linkInBento">
                  Visit the Security Lab
                </Link>
              </Bento.Content>
              <Bento.Visual
                fillMedia={false}
                padding={{xsmall: 'normal', medium: 'spacious'}}
                className="lp-Features-bentoItem4-visual"
              >
                <Image
                  src="/images/modules/site/enterprise/advanced-security/features-bento-4.webp"
                  alt="Insights and remediation advice for a critical Log4j vulnerability as documented in the GitHub Advisory Database"
                  width="566"
                  height="402"
                  loading="lazy"
                  className="lp-Features-bentoItem4-img lp-borderRadiusNone"
                />
              </Bento.Visual>
            </Bento.Item>
          </Bento>
        </div>
      </section>

      <section id="pricing" className="lp-Section--pricing">
        <div className="lp-Container">
          <div className="lp-Spacer--pricing" aria-hidden />

          <SectionIntro align="center" fullWidth>
            <SectionIntro.Label size="large" className="lp-SectionIntroLabel--muted">
              Pricing
            </SectionIntro.Label>
            <SectionIntro.Heading as="h2" size="2">
              Enable native security <br /> for every repository.
            </SectionIntro.Heading>
            <SectionIntro.Description className="lp-Pricing-sectionIntroDescription">
              Eliminate toolchain cost and complexity with native DevSecOps tools for
              <br className="lp-break-whenMedium" /> GitHub Enterprise and Azure DevOps.
            </SectionIntro.Description>
          </SectionIntro>

          <div className="lp-Spacer" style={{'--lp-space': '40px'} as React.CSSProperties} aria-hidden />

          <Stack
            className="lp-Pricing-card"
            direction={{narrow: 'vertical', regular: 'horizontal'}}
            gap="none"
            padding="none"
          >
            {/* Standard */}

            <Stack padding="none" gap={{narrow: 16, regular: 24}} style={{flex: '1'}}>
              <Box>
                <Text size="100" weight="semibold" className="lp-Pricing-label">
                  Included with all plans
                </Text>
                <Box marginBlockStart={8} marginBlockEnd={16}>
                  <Heading as="h3" size="5">
                    Standard Security
                  </Heading>
                </Box>
                <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description">
                  Manage and secure open source components and public repositories
                </Text>
              </Box>

              <Box>
                <Stack direction="horizontal" gap={8} padding="none" className="lp-Pricing-price">
                  <Text size="500" weight="normal" className="lp-is-sansSerifAlt" style={{lineHeight: 1.3}}>
                    $
                  </Text>
                  <Text weight="normal" className="lp-is-sansSerifAlt" style={{fontSize: '56px', lineHeight: 1}}>
                    0
                  </Text>
                  <Text
                    size="500"
                    weight="normal"
                    className="lp-is-sansSerifAlt"
                    style={{lineHeight: 1.1, alignSelf: 'end', marginLeft: '8px'}}
                  >
                    USD
                  </Text>
                </Stack>
                <Text as="div" size="200" weight="normal" variant="muted" style={{marginTop: '8px'}}>
                  For all users and plans
                </Text>
              </Box>

              <Button
                as="a"
                href="https://docs.github.com/code-security/getting-started/github-security-features"
                block
              >
                Learn more
              </Button>

              <hr className="lp-Pricing-divider" />

              <div>
                <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-listTitle">
                  What’s included:
                </Text>

                <input type="checkbox" id="pricing-standard" className="lp-Pricing-listToggleCheckbox" />
                <label htmlFor="pricing-standard" className="lp-Pricing-listToggle">
                  What’s included <ChevronDownIcon className="lp-Pricing-listToggleIcon" />
                </label>

                <div className="lp-Pricing-container">
                  <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-groupTitle">
                    Code Scanning
                  </Text>
                  <UnorderedList variant="checked" className="lp-Pricing-list">
                    <UnorderedList.Item>Code scanning for public repositories</UnorderedList.Item>
                  </UnorderedList>
                  <UnorderedList
                    className="lp-Pricing-list lp-Pricing-list--excluded"
                    aria-label="What’s not included:"
                  >
                    <UnorderedList.Item>
                      <XIcon />
                      Quick autofixes with AI-powered code suggestions**
                    </UnorderedList.Item>
                    <UnorderedList.Item>
                      <XIcon />
                      Contextual vulnerability intelligence and advice
                    </UnorderedList.Item>
                    <UnorderedList.Item>
                      <XIcon />
                      Hunt zero-day threats and their variants
                    </UnorderedList.Item>
                  </UnorderedList>

                  <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-groupTitle">
                    Secret Scanning
                  </Text>
                  <UnorderedList variant="checked" className="lp-Pricing-list">
                    <UnorderedList.Item>Find secrets in public repositories only</UnorderedList.Item>
                    <UnorderedList.Item>Block secrets on pushes to public repositories</UnorderedList.Item>
                  </UnorderedList>
                  <UnorderedList
                    className="lp-Pricing-list lp-Pricing-list--excluded"
                    aria-label="What’s not included:"
                  >
                    <UnorderedList.Item>
                      <XIcon />
                      Revoke and notify on leaked secrets
                    </UnorderedList.Item>
                  </UnorderedList>

                  <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-groupTitle">
                    Supply Chain
                  </Text>
                  <UnorderedList variant="checked" className="lp-Pricing-list">
                    <UnorderedList.Item>Identify and update vulnerable open source components</UnorderedList.Item>
                    <UnorderedList.Item>Access intelligence in the GitHub Advisory Database</UnorderedList.Item>
                    <UnorderedList.Item>Report vulnerabilities to open source maintainers</UnorderedList.Item>
                    <UnorderedList.Item>Generate and export SBOMs</UnorderedList.Item>
                    <UnorderedList.Item>Manage transitive dependencies with submission API</UnorderedList.Item>
                    <UnorderedList.Item>Detect calls to vulnerable functions (public repositories)</UnorderedList.Item>
                  </UnorderedList>
                  <UnorderedList
                    className="lp-Pricing-list lp-Pricing-list--excluded"
                    aria-label="What’s not included:"
                  >
                    <UnorderedList.Item>
                      <XIcon />
                      Define and enforce auto-triage rules
                    </UnorderedList.Item>
                  </UnorderedList>

                  <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-groupTitle">
                    Administration
                  </Text>
                  <UnorderedList
                    className="lp-Pricing-list lp-Pricing-list--excluded"
                    aria-label="What’s not included:"
                  >
                    <UnorderedList.Item>
                      <XIcon />
                      View security metrics and insights
                    </UnorderedList.Item>
                    <UnorderedList.Item>
                      <XIcon />
                      Assess feature adoption and code security risk
                    </UnorderedList.Item>
                    <UnorderedList.Item>
                      <XIcon />
                      Enable security features for multiple repositories
                    </UnorderedList.Item>
                  </UnorderedList>
                </div>
              </div>
            </Stack>

            <hr className="lp-Pricing-divider lp-Pricing-divider--vertical" />

            {/* Advanced */}

            <Stack padding="none" gap={{narrow: 16, regular: 24}} style={{flex: '1'}}>
              <Box>
                <Text size="100" weight="semibold" className="lp-Pricing-label">
                  Requires GitHub Enterprise or Azure DevOps
                </Text>
                <Box marginBlockStart={8} marginBlockEnd={16}>
                  <Heading as="h3" size="5">
                    GitHub Advanced Security
                  </Heading>
                </Box>
                <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description">
                  Detect, prevent, and remediate vulnerabilities in all public and private repositories
                </Text>
              </Box>

              <Box>
                <Stack direction="horizontal" gap={8} padding="none" className="lp-Pricing-price">
                  <Text size="500" weight="normal" className="lp-is-sansSerifAlt" style={{lineHeight: 1.3}}>
                    $
                  </Text>
                  <Text weight="normal" className="lp-is-sansSerifAlt" style={{fontSize: '56px', lineHeight: 1}}>
                    49
                  </Text>
                  <Text
                    size="500"
                    weight="normal"
                    className="lp-is-sansSerifAlt"
                    style={{lineHeight: 1.1, alignSelf: 'end', marginLeft: '8px'}}
                  >
                    USD
                  </Text>
                </Stack>
                <Text as="div" size="200" weight="normal" variant="muted" style={{marginTop: '8px'}}>
                  per month / per active committer
                </Text>
              </Box>

              <Button as="a" href={pricingContactSalesPath} variant="primary">
                Contact sales
              </Button>

              <hr className="lp-Pricing-divider" />

              <div>
                <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-listTitle">
                  What’s included:
                </Text>

                <input type="checkbox" id="pricing-advanced" className="lp-Pricing-listToggleCheckbox" />
                <label htmlFor="pricing-advanced" className="lp-Pricing-listToggle">
                  What’s included <ChevronDownIcon className="lp-Pricing-listToggleIcon" />
                </label>

                <div className="lp-Pricing-container">
                  <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-groupTitle">
                    Code Scanning
                  </Text>
                  <UnorderedList variant="checked" className="lp-Pricing-list">
                    <UnorderedList.Item>Code scanning for public repositories</UnorderedList.Item>
                    <UnorderedList.Item>Quick autofixes with AI-powered code suggestions**</UnorderedList.Item>
                    <UnorderedList.Item>Contextual vulnerability intelligence and advice</UnorderedList.Item>
                    <UnorderedList.Item>Hunt zero-day threats and their variants</UnorderedList.Item>
                  </UnorderedList>

                  <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-groupTitle">
                    Secret Scanning
                  </Text>
                  <UnorderedList variant="checked" className="lp-Pricing-list">
                    <UnorderedList.Item>Find secrets in public repositories only</UnorderedList.Item>
                    <UnorderedList.Item>Block secrets on pushes to public repositories</UnorderedList.Item>
                    <UnorderedList.Item>Revoke and notify on leaked secrets</UnorderedList.Item>
                  </UnorderedList>

                  <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-groupTitle">
                    Supply Chain
                  </Text>
                  <UnorderedList variant="checked" className="lp-Pricing-list">
                    <UnorderedList.Item>Identify and update vulnerable open source components</UnorderedList.Item>
                    <UnorderedList.Item>Access intelligence in the GitHub Advisory Database</UnorderedList.Item>
                    <UnorderedList.Item>Report vulnerabilities to open source maintainers</UnorderedList.Item>
                    <UnorderedList.Item>Generate and export SBOMs</UnorderedList.Item>
                    <UnorderedList.Item>Manage transitive dependencies with submission API</UnorderedList.Item>
                    <UnorderedList.Item>Detect calls to vulnerable functions (public repositories)</UnorderedList.Item>
                    <UnorderedList.Item>Define and enforce auto-triage rules</UnorderedList.Item>
                  </UnorderedList>

                  <Text size="100" weight="semibold" variant="muted" className="lp-Pricing-groupTitle">
                    Administration
                  </Text>
                  <UnorderedList variant="checked" className="lp-Pricing-list">
                    <UnorderedList.Item>View security metrics and insights</UnorderedList.Item>
                    <UnorderedList.Item>Assess feature adoption and code security risk</UnorderedList.Item>
                    <UnorderedList.Item>Enable security features for multiple repositories</UnorderedList.Item>
                  </UnorderedList>

                  <Text size="100" variant="muted" className="lp-Pricing-footnote">
                    ** Only with GitHub Advanced Security on GitHub Enterprise. Public beta in March, 2024.
                  </Text>
                </div>
              </div>
            </Stack>
          </Stack>
        </div>
      </section>

      <section id="resources" className="lp-Section--resources">
        <div className="lp-Container">
          <div
            className="lp-Spacer"
            style={{'--lp-space': '64px', '--lp-space-xl': '104px'} as React.CSSProperties}
            aria-hidden
          />

          <SectionIntro align="center" fullWidth>
            <SectionIntro.Heading size="3">
              Get the most out of <br className="lp-break-whenWide" /> GitHub Advanced Security.
            </SectionIntro.Heading>
          </SectionIntro>

          <div className="lp-Spacer" style={{'--lp-space': '40px'} as React.CSSProperties} aria-hidden />

          <Stack
            direction={{narrow: 'vertical', regular: 'horizontal', wide: 'horizontal'}}
            gap="normal"
            padding="none"
          >
            <div className="lp-Resources-card">
              <Card
                ctaText="Get started with GHAS"
                href="https://docs.github.com/code-security/getting-started/github-security-features"
              >
                <Card.Icon icon={<BookIcon />} color="blue" hasBackground />
                <Card.Heading>How to get started with GitHub Advanced Security (GHAS)</Card.Heading>
                <Card.Description>
                  Learn about GitHub Advanced Security and how it can benefit your organization.
                </Card.Description>
              </Card>
            </div>
            <div className="lp-Resources-card">
              <Card
                ctaText="Read the Forrester Report"
                href="https://resources.github.com/forrester-industry-spotlight-github-advanced-security/"
              >
                <Card.Icon icon={<BookIcon />} color="blue" hasBackground />
                <Card.Heading>GitHub TEI spotlight for GitHub Advanced Security</Card.Heading>
                <Card.Description>
                  Read about the benefits of improving software security standards in organizations.
                </Card.Description>
              </Card>
            </div>
            <div className="lp-Resources-card">
              <Card
                ctaText="Watch the videos"
                href="https://resources.github.com/security/what-is-github-advanced-security/"
              >
                <Card.Icon icon={<BookIcon />} color="blue" hasBackground />
                <Card.Heading>Security playlist</Card.Heading>
                <Card.Description>
                  Learn how industry experts use GitHub Advanced Security to protect their code without sacrificing
                  developer productivity.
                </Card.Description>
              </Card>
            </div>
          </Stack>
        </div>
      </section>

      <section id="faq" className="lp-Section--faqs">
        <div className="lp-Container">
          <div
            className="lp-Spacer"
            style={{'--lp-space': '72px', '--lp-space-xl': '128px'} as React.CSSProperties}
            aria-hidden
          />

          <FAQGroup>
            <FAQGroup.Heading align="center">Frequently asked questions.</FAQGroup.Heading>

            <FAQ>
              <FAQ.Heading>Group 1</FAQ.Heading>
              <FAQ.Item>
                <FAQ.Question>Question 1</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 1</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 2</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 2</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 3</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 3</p>
                </FAQ.Answer>
              </FAQ.Item>
            </FAQ>

            <FAQ>
              <FAQ.Heading>Group 2</FAQ.Heading>
              <FAQ.Item>
                <FAQ.Question>Question 1</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 1</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 2</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 2</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 3</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 3</p>
                </FAQ.Answer>
              </FAQ.Item>
            </FAQ>
          </FAQGroup>

          <div
            className="lp-Spacer"
            style={{'--lp-space': '72px', '--lp-space-xl': '128px'} as React.CSSProperties}
            aria-hidden
          />
        </div>
      </section>

      <section id="footnotes" className="lp-Section--footnotes">
        <div className="lp-Container">
          <OrderedList>
            <OrderedList.Item className="lp-Footnotes-item">
              Based on data from the industry’s longest running analysis of fix rates, Veracode State of Software
              Security 2023. Developers with GitHub Advanced Security fix 48% of vulnerabilities in real time, more than
              7x faster than the industry average, where it takes 198 days to reach a 50% fix rate.
            </OrderedList.Item>
            <OrderedList.Item className="lp-Footnotes-item">
              A Comparative Study of Software Secrets Reporting by Secret Detection Tools, Setu Kumar Basak et al.,
              North Carolina State University, 2023
            </OrderedList.Item>
          </OrderedList>

          <div
            className="lp-Spacer"
            style={{'--lp-space': '72px', '--lp-space-xl': '128px'} as React.CSSProperties}
            aria-hidden
          />
        </div>
      </section>
    </ThemeProvider>
  )
}

try{ EnterpriseAdvancedSecurityIndex.displayName ||= 'EnterpriseAdvancedSecurityIndex' } catch {}